import {
  CustomQuestionnaire,
  CustomQuestionnaireAnswer,
  CustomQuestionnaireAnswerSet,
  CustomQuestionnaireAnswerSetSummary,
  CustomQuestionnaireSummary,
} from '@api';
import { action, makeObservable, observable } from 'mobx';

export type CustomQuestionnaireAnswerExtended = CustomQuestionnaireAnswer & { skip: boolean };

export interface CustomQuestionnaireAnswerSetExtended extends Omit<CustomQuestionnaireAnswerSet, 'answers'> {
  answers: CustomQuestionnaireAnswerExtended[];
  answerSetVersion?: number;
  useCaseName?: string;
  lastConfirmedStep?: number; // Client-side only: tracks the furthest confirmed step
  // useCaseId?: number;
}

class CustomQuestionnaireStore {
  currentStep: number = 1;

  maxStep: number = 1;

  lastConfirmedStep: number = 1;

  questionnaireData: CustomQuestionnaireAnswerSetExtended = null;

  customQuestionnaires: CustomQuestionnaireSummary[] = [];

  customQuestionnaire: CustomQuestionnaire = null;

  customQuestionnaireAnswerSet: CustomQuestionnaireAnswerSet = null;

  questionnaireAnswerSets: CustomQuestionnaireAnswerSetSummary[] = null;

  completed: boolean = false;

  showAllCustomQuestionnaire: boolean = true;

  stepPrefillInProgress: boolean = false;

  hasUnsavedAnswers: boolean = false;

  showChangeVersionDialog: boolean = false;

  constructor() {
    makeObservable(this, {
      currentStep: observable,
      maxStep: observable,
      lastConfirmedStep: observable,
      completed: observable,
      questionnaireData: observable,
      customQuestionnaireAnswerSet: observable,
      customQuestionnaire: observable,
      customQuestionnaires: observable,
      questionnaireAnswerSets: observable,
      showAllCustomQuestionnaire: observable,
      stepPrefillInProgress: observable,
      hasUnsavedAnswers: observable,
      showChangeVersionDialog: observable,
      setCurrentStep: action.bound,
      setMaxStep: action.bound,
      setLastConfirmedStep: action.bound,
      setCompleted: action.bound,
      setCustomQuestionnaireAnswerSet: action.bound,
      setCustomQuestionnaire: action.bound,
      setCustomQuestionnaires: action.bound,
      setQuestionnaireAnswerSets: action.bound,
      setShowAllCustomQuestionnaire: action.bound,
      setStepPrefillInProgress: action.bound,
      setHasUnsavedAnswers: action.bound,
      setShowChangeVersionDialog: action.bound,
      setQuestionnaireData: action.bound,
      addQuestionnaireData: action.bound,
      updateQuestionnaireAnswers: action.bound,
      resetQuestionnaire: action.bound,
      resetQuestionnaireSteps: action.bound,
    });
  }

  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  setMaxStep(step: number) {
    this.maxStep = step;
  }

  setLastConfirmedStep(step: number) {
    this.lastConfirmedStep = step;
  }

  setCompleted(completed: boolean) {
    this.completed = completed;
  }

  setShowAllCustomQuestionnaire(show: boolean) {
    this.showAllCustomQuestionnaire = show;
  }

  setStepPrefillInProgress(value: boolean) {
    this.stepPrefillInProgress = value;
  }

  setHasUnsavedAnswers(value: boolean) {
    this.hasUnsavedAnswers = value;
  }

  setShowChangeVersionDialog(value: boolean) {
    this.showChangeVersionDialog = value;
  }

  setQuestionnaireData(data: CustomQuestionnaireAnswerSetExtended) {
    this.questionnaireData = data;
  }

  setCustomQuestionnaires(data: any) {
    this.customQuestionnaires = data;
  }

  setCustomQuestionnaireAnswerSet(data: CustomQuestionnaireAnswerSet) {
    this.customQuestionnaireAnswerSet = data;
  }

  addQuestionnaireData(data: Partial<CustomQuestionnaireAnswerSetExtended>) {
    this.questionnaireData = { ...this.questionnaireData, ...data };
  }

  updateQuestionnaireAnswers(answers: CustomQuestionnaireAnswerExtended[]) {
    if (!this.questionnaireData) {
      return;
    }
    if (!this.questionnaireData.answers) {
      this.questionnaireData.answers = [];
    }
    answers.forEach(answer => {
      const existingAnswerIndex = this.questionnaireData.answers.findIndex(a => a.questionId === answer.questionId);
      if (existingAnswerIndex !== -1) {
        this.questionnaireData.answers[existingAnswerIndex] = answer;
      } else {
        this.questionnaireData.answers.push(answer);
      }
    });
  }

  setCustomQuestionnaire(data: CustomQuestionnaire) {
    this.customQuestionnaire = data;
  }

  setQuestionnaireAnswerSets(data: CustomQuestionnaireAnswerSetSummary[]) {
    this.questionnaireAnswerSets = data;
  }

  resetQuestionnaire() {
    this.questionnaireData = null;
    this.questionnaireAnswerSets = null;
    this.showAllCustomQuestionnaire = true;
    this.currentStep = 1;
    this.maxStep = 1;
    this.lastConfirmedStep = 1;
    this.completed = false;
    this.stepPrefillInProgress = false;
    this.hasUnsavedAnswers = false;
    this.showChangeVersionDialog = false;
  }

  resetQuestionnaireSteps() {
    this.currentStep = 1;
    this.maxStep = 1;
    this.lastConfirmedStep = 1;
    this.stepPrefillInProgress = false;
  }
}

export const customQuestionnaireStore = new CustomQuestionnaireStore();
